import { ArrowBack, InfoOutlined } from "@mui/icons-material";
import { Box, Button, Card, CardContent, CircularProgress, Divider, FormControlLabel, IconButton, Switch, Tab, Tabs, TextField, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import locales from "../../../constants/locales";
import Loading from "../../../components/loading/loading";
import OrgSelector from "../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import { black, greyEnabled, orange, orangeLightActive, purple } from "../../../theme/colors";
import { cms_amiforyou } from "../../../routes/routes_path";
import { createAmiForYou, getAmiForYouById, updateAmiForYou } from "../../../services/cms/cms";
import IconPicker from "../components/icon_picker/icon_picker";
import TargetingFields, { buildTargetingPayload, defaultTargeting, targetingFromResponse, TargetingFormValues } from "../components/targeting_fields/targeting_fields";
import { AmiForYou } from "../models/cms";

interface AmiForYouFormValues {
  title: string;
  description: string;
  status: boolean;
  icon_url: string;
  link: string;
  targeting: TargetingFormValues;
}

const defaultValues: AmiForYouFormValues = {
  title: "",
  description: "",
  status: true,
  icon_url: "",
  link: "",
  targeting: defaultTargeting,
};

const ICON_ASPECT_RATIO = "1:1";
const ICON_SIZE_LABEL = "100 x 100 px";

// A small bold, uppercase divider label — breaks the form into scannable
// groups instead of one long flat list of fields.
function SectionHeader({ children, mt = 4 }: { children: ReactNode; mt?: number }) {
  return (
    <Box mt={mt} mb={1.5}>
      <Typography variant="overline" fontWeight={700} color={purple} sx={{ letterSpacing: 0.6 }}>
        {children}
      </Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );
}

export default function AddEditAmiForYou({ viewMode = false }: { viewMode?: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSnackbar } = useFeedback();
  const isEdit = !!id;
  // AMI For You is always created in English; other languages are added
  // afterwards from the edit/view screen, same convention as News.
  const [selectedLocale, setSelectedLocale] = useState("en");
  const { control, handleSubmit, watch, setValue, reset } = useForm<AmiForYouFormValues>({
    defaultValues,
  });
  const { selectedOrgId } = useSelectedOrgStore();

  const { data: itemDetails, isLoading: isItemLoading } = useQuery<AmiForYou | null>(
    ["cms-amiforyou-details", id, selectedLocale],
    async () => {
      const response = await getAmiForYouById(id!, selectedLocale);
      return response?.data?.data;
    },
    { enabled: !!id }
  );

  useEffect(() => {
    if (itemDetails) {
      reset({
        title: itemDetails.title || "",
        description: itemDetails.description || "",
        status: itemDetails.status ?? true,
        icon_url: itemDetails.icon_url || "",
        link: itemDetails.link || "",
        targeting: targetingFromResponse(itemDetails.targeting),
      });
    }
  }, [itemDetails, reset]);

  const title = watch("title");
  const iconUrl = watch("icon_url");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = (values: AmiForYouFormValues) => {
    setIsSubmitting(true);
    const payload = {
      title: values.title,
      description: values.description || "",
      status: values.status,
      icon_url: values.icon_url || null,
      link: values.link || null,
      targeting: buildTargetingPayload(values.targeting),
      ...(isEdit ? {} : { orgId: selectedOrgId || undefined }),
    };

    const request = isEdit ? updateAmiForYou(id!, selectedLocale, payload) : createAmiForYou(payload);
    request
      .then(() => {
        showSnackbar(isEdit ? "AMI For You updated successfully" : "AMI For You added successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-amiforyou"] });
        // The list query above doesn't cover this item's own details cache
        // (keyed separately by id+locale) - without this, re-opening Edit
        // within the 5-minute default staleTime serves the stale
        // pre-update data until a hard refresh.
        queryClient.invalidateQueries({ queryKey: ["cms-amiforyou-details", id] });
        navigate(cms_amiforyou);
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <MainLayout>
      <Card sx={{ width: "100%", borderRadius: 4, m: 1 }} elevation={1}>
        <CardContent>
          <Box display="flex" flexDirection="row" alignItems="center">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" fontWeight="600" ml={1}>
              {viewMode ? "View AMI For You" : isEdit ? "Edit AMI For You" : "Add AMI For You"}
            </Typography>
          </Box>
          <Divider sx={{ my: 3, opacity: 0.5, borderBottom: "1px dotted #7C797C" }} />

          {isEdit && isItemLoading ? (
            <Loading label="Loading AMI For You..." />
          ) : (
          <>
          {!isEdit && !viewMode && (
            <Box maxWidth={360}>
              <OrgSelector label="Organisation" fullWidth lockToOwnOrg platformDefaultsToOwnOrg />
            </Box>
          )}

          {isEdit && (
            <>
              <Tabs
                value={locales.findIndex((locale) => locale.code === selectedLocale)}
                onChange={(_, index: number) => setSelectedLocale(locales[index].code)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ style: { backgroundColor: purple, height: 3, borderRadius: 3 } }}
                sx={{ mt: 2 }}
              >
                {locales.map((locale) => (
                  <Tab
                    key={locale.code}
                    label={locale.name}
                    sx={{ textTransform: "capitalize", minWidth: 90, color: greyEnabled, "&.Mui-selected": { color: purple } }}
                  />
                ))}
              </Tabs>
              <Divider />

              {itemDetails?.is_translated === false && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: orangeLightActive, border: `1px solid ${orangeLightActive}` }}
                >
                  <InfoOutlined sx={{ color: orange, fontSize: 20 }} />
                  <Typography variant="body2" color={black}>
                    This item has no content in{" "}
                    {locales.find((locale) => locale.code === selectedLocale)?.name || selectedLocale} yet — fill in the
                    details below to add this translation.
                  </Typography>
                </Box>
              )}
            </>
          )}

          <SectionHeader mt={!isEdit && !viewMode ? 3 : 2}>Basic Details</SectionHeader>

          <Controller
            name="title"
            control={control}
            rules={{
              required: "Title is required",
              minLength: { value: 3, message: "Title must be at least 3 characters" },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                required
                label={<>Title <span style={{ color: "red" }}>*</span></>}
                margin="normal"
                disabled={viewMode}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={4}
                label="Description"
                margin="normal"
                disabled={viewMode}
              />
            )}
          />

          <Controller
            name="status"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={viewMode}
                  />
                }
                label={field.value ? "Active" : "Inactive"}
              />
            )}
          />

          <SectionHeader>Icon</SectionHeader>

          <Box display="flex" flexDirection="row" flexWrap="wrap" gap={4}>
            <Controller
              name="icon_url"
              control={control}
              render={({ field }) => (
                <IconPicker
                  label="Icon"
                  required
                  value={field.value}
                  disabled={viewMode}
                  onChange={field.onChange}
                  recommendedAspectRatio={ICON_ASPECT_RATIO}
                  recommendedSizeLabel={ICON_SIZE_LABEL}
                />
              )}
            />
          </Box>

          <SectionHeader>Link</SectionHeader>

          <Controller
            name="link"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Link (URL)" margin="normal" disabled={viewMode} />
            )}
          />

          <SectionHeader>Audience</SectionHeader>

          <TargetingFields control={control} setValue={setValue} viewMode={viewMode} />

          <Box display="flex" flexDirection="row" justifyContent="space-between" mt={5}>
            <Button onClick={() => navigate(-1)} variant="outlined" sx={{ color: black }} disabled={isSubmitting}>
              {viewMode ? "Back" : "Cancel"}
            </Button>
            {!viewMode && (
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting || !title || title.trim().length < 3 || !iconUrl}
                variant="contained"
                startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : undefined}
              >
                {isSubmitting ? (isEdit ? "Updating..." : "Adding...") : isEdit ? "Update" : "Add"}
              </Button>
            )}
          </Box>
          </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
}
